import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Header from '../components/Header';
import {
  formatMinistryMonthLabel,
  mbcbMinistryPeriodCaption,
  mbcbNamingExampleFromToday,
  mbcbReportNamedTitle,
} from '../utils/mbcbReportPeriodLabels';
import { createMyMbcbReport, getDefaultMinistryPeriod } from '../api/mbcbReportService';
import {
  fetchMyPlanterProfile,
  fetchReports,
  reportActionLabel,
  statusBadgeClasses,
  statusDisplayLabel,
  type MBCBReport,
} from '../utils/mbcbReportShared';

function groupReportsByYear(reports: MBCBReport[]): Map<number, MBCBReport[]> {
  const map = new Map<number, MBCBReport[]>();
  const sorted = [...reports].sort((a, b) => {
    if (b.reportYear !== a.reportYear) return b.reportYear - a.reportYear;
    return b.reportMonth - a.reportMonth;
  });
  for (const r of sorted) {
    const list = map.get(r.reportYear) ?? [];
    list.push(r);
    map.set(r.reportYear, list);
  }
  return map;
}

const MBCBReports: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const namingExample = mbcbNamingExampleFromToday();
  const defaultMinistryPeriod = useMemo(() => getDefaultMinistryPeriod(), []);

  const { data: planter, isLoading: planterLoading, error: planterError } = useQuery({
    queryKey: ['myPlanterProfile'],
    queryFn: fetchMyPlanterProfile,
    retry: false,
  });

  const planterId = planter?.id as number | undefined;

  const { data: reports, isLoading: reportsLoading } = useQuery({
    queryKey: ['mbcbReports', 'me'],
    queryFn: fetchReports,
    enabled: !!planterId,
  });

  useEffect(() => {
    const reportIdParam = searchParams.get('reportId');
    if (!reportIdParam) return;
    const id = parseInt(reportIdParam, 10);
    if (Number.isFinite(id)) {
      navigate(`/mbcb/report/${id}`, { replace: true });
    }
  }, [searchParams, navigate]);

  const reportingDisabled = planter?.mbcbReportingEnabled === false;

  const existingPeriodKeys = useMemo(() => {
    const set = new Set<string>();
    (reports ?? []).forEach((r) => set.add(`${r.reportYear}-${r.reportMonth}`));
    return set;
  }, [reports]);

  const availablePeriods = useMemo(() => {
    const out: { key: string; month: number; year: number }[] = [];
    let m = defaultMinistryPeriod.reportMonth;
    let y = defaultMinistryPeriod.reportYear;
    for (let i = 0; i < 24; i += 1) {
      const key = `${y}-${m}`;
      if (!existingPeriodKeys.has(key)) out.push({ key, month: m, year: y });
      if (m === 1) {
        m = 12;
        y -= 1;
      } else {
        m -= 1;
      }
    }
    return out;
  }, [defaultMinistryPeriod, existingPeriodKeys]);

  const defaultPeriodKey = `${defaultMinistryPeriod.reportYear}-${defaultMinistryPeriod.reportMonth}`;

  const currentPeriodReport = useMemo(
    () =>
      (reports ?? []).find(
        (r) => `${r.reportYear}-${r.reportMonth}` === defaultPeriodKey,
      ),
    [reports, defaultPeriodKey],
  );

  const [showNamingHelp, setShowNamingHelp] = useState(false);
  const [showCatchUp, setShowCatchUp] = useState(false);
  const [selectedPeriodKey, setSelectedPeriodKey] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);

  const catchUpPeriods = useMemo(
    () => availablePeriods.filter((p) => p.key !== defaultPeriodKey),
    [availablePeriods, defaultPeriodKey],
  );

  const effectiveSelectedKey =
    selectedPeriodKey && catchUpPeriods.some((p) => p.key === selectedPeriodKey)
      ? selectedPeriodKey
      : catchUpPeriods[0]?.key ?? '';

  const createReportMutation = useMutation({
    mutationFn: (period: { reportMonth: number; reportYear: number }) => createMyMbcbReport(period),
    onMutate: () => setCreateError(null),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['mbcbReports', 'me'] });
      navigate(`/mbcb/report/${data.id}`);
    },
    onError: (err: unknown) => {
      const resp = (
        err as {
          response?: {
            data?: { data?: { reportId?: number; existingReportId?: number }; message?: string };
          };
        }
      ).response;
      const existingId = resp?.data?.data?.reportId ?? resp?.data?.data?.existingReportId;
      if (existingId) {
        queryClient.invalidateQueries({ queryKey: ['mbcbReports', 'me'] });
        navigate(`/mbcb/report/${existingId}`);
        return;
      }
      setCreateError(resp?.data?.message || 'Could not create that report. Please try again.');
    },
  });

  const createForPeriod = (period: { reportMonth: number; reportYear: number }) => {
    createReportMutation.mutate(period);
  };

  const createSelectedCatchUp = () => {
    const period = catchUpPeriods.find((p) => p.key === effectiveSelectedKey);
    if (period) createForPeriod({ reportMonth: period.month, reportYear: period.year });
  };

  const openReport = (id: number) => navigate(`/mbcb/report/${id}`);

  if (planterLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-lg mx-auto p-6 flex justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
        </div>
      </div>
    );
  }

  if (planterError || !planterId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-lg mx-auto p-6">
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-4 rounded-xl">
            <p className="font-medium">MBCB Reporting Not Available</p>
            <p className="text-sm mt-1">
              You do not have a church planter profile yet. Once your hub leader marks you green, your
              monthly reporting will be available here.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const incompleteReports = (reports ?? []).filter(
    (r) => r.status === 'draft' || r.status === 'needs_revision',
  );

  const otherIncomplete = incompleteReports.filter(
    (r) => `${r.reportYear}-${r.reportMonth}` !== defaultPeriodKey,
  );

  const reportsByYear = groupReportsByYear(reports ?? []);
  const defaultMinistryLabel = formatMinistryMonthLabel(
    defaultMinistryPeriod.reportMonth,
    defaultMinistryPeriod.reportYear,
  );
  const defaultFilingTitle = mbcbReportNamedTitle(
    defaultMinistryPeriod.reportMonth,
    defaultMinistryPeriod.reportYear,
  );

  const heroIsDraft =
    currentPeriodReport?.status === 'draft' || currentPeriodReport?.status === 'needs_revision';
  const heroNeedsRevision = currentPeriodReport?.status === 'needs_revision';

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-lg mx-auto p-4 sm:p-6 space-y-5 pb-20">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Monthly reports</h1>
          <p className="text-sm text-gray-500 mt-1">
            Share your ministry story — {defaultMinistryLabel} ministry
          </p>
          <button
            type="button"
            onClick={() => setShowNamingHelp((v) => !v)}
            className="mt-2 text-xs font-medium text-primary-600 hover:text-primary-700"
          >
            {showNamingHelp ? 'Hide' : 'How report naming works'}
          </button>
          {showNamingHelp && (
            <p className="mt-2 text-xs text-gray-600 leading-relaxed bg-white rounded-xl border border-gray-100 p-3">
              Each report is <strong>named for the month you submit it</strong> (for example, the{' '}
              <strong>{namingExample.filingLabel}</strong> report). The questions and numbers are always
              about the <strong>previous calendar month</strong> of ministry (for example,{' '}
              <strong>{namingExample.ministryLabel}</strong>).
            </p>
          )}
          {reportingDisabled && (
            <div className="mt-3 bg-amber-50 border border-amber-200 text-amber-900 text-sm p-3 rounded-xl">
              Reporting is currently turned off for your account. Contact your administrator if this is a
              mistake.
            </div>
          )}
        </div>

        {!reportingDisabled && (
          <div
            className={`rounded-2xl p-5 shadow-sm border-2 ${
              heroNeedsRevision
                ? 'border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50/50'
                : 'border-primary-200 bg-gradient-to-br from-primary-50 to-indigo-50/40'
            }`}
          >
            {currentPeriodReport ? (
              <>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {heroNeedsRevision ? 'Needs your attention' : heroIsDraft ? 'In progress' : 'Current report'}
                </p>
                <h2 className="text-lg font-bold text-gray-900 mt-1">{defaultFilingTitle}</h2>
                <p className="text-sm text-gray-600 mt-0.5">
                  {mbcbMinistryPeriodCaption(
                    defaultMinistryPeriod.reportMonth,
                    defaultMinistryPeriod.reportYear,
                  )}
                </p>
                <span
                  className={`inline-flex mt-2 rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${statusBadgeClasses(
                    currentPeriodReport.status,
                  )}`}
                >
                  {statusDisplayLabel(currentPeriodReport.status)}
                </span>
                <button
                  type="button"
                  onClick={() => openReport(currentPeriodReport.id)}
                  className={`mt-4 w-full py-3.5 rounded-xl text-sm font-semibold text-white min-h-touch shadow-md ${
                    heroNeedsRevision
                      ? 'bg-amber-600 hover:bg-amber-700 shadow-amber-600/20'
                      : heroIsDraft
                        ? 'bg-primary-600 hover:bg-primary-700 shadow-primary-600/20'
                        : 'bg-gray-800 hover:bg-gray-900 shadow-gray-800/20'
                  }`}
                >
                  {reportActionLabel(currentPeriodReport.status)}
                </button>
              </>
            ) : (
              <>
                <p className="text-xs font-medium text-primary-700 uppercase tracking-wide">Ready to start</p>
                <h2 className="text-lg font-bold text-gray-900 mt-1">{defaultFilingTitle}</h2>
                <p className="text-sm text-gray-600 mt-0.5">
                  Reflect on {defaultMinistryLabel} — takes about 5–10 minutes.
                </p>
                <button
                  type="button"
                  onClick={() => createForPeriod(defaultMinistryPeriod)}
                  disabled={createReportMutation.isPending}
                  className="mt-4 w-full py-3.5 rounded-xl bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50 min-h-touch shadow-md shadow-primary-600/20"
                >
                  {createReportMutation.isPending ? 'Creating…' : `Start ${defaultFilingTitle}`}
                </button>
              </>
            )}
          </div>
        )}

        {otherIncomplete.length > 0 && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 space-y-2">
            <h2 className="text-sm font-semibold text-gray-900">Other drafts</h2>
            <ul className="space-y-2">
              {otherIncomplete.map((r) => (
                <li key={r.id}>
                  <Link
                    to={`/mbcb/report/${r.id}`}
                    className="flex items-center justify-between gap-3 rounded-xl bg-white border border-amber-100 px-4 py-3 min-h-touch"
                  >
                    <div className="min-w-0">
                      <span className="font-semibold text-gray-900 text-sm block truncate">
                        {mbcbReportNamedTitle(r.reportMonth, r.reportYear)}
                      </span>
                      <span className="text-xs text-gray-600">
                        {r.status === 'needs_revision' ? 'Needs revision' : 'Draft in progress'}
                      </span>
                    </div>
                    <svg className="w-5 h-5 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <h2 className="text-sm font-semibold text-gray-900 mb-3">All reports</h2>
          {reportsLoading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : reports && reports.length > 0 ? (
            <div className="space-y-5">
              {Array.from(reportsByYear.entries()).map(([yr, yearReports]) => (
                <div key={yr}>
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">{yr}</p>
                  <ul className="space-y-2">
                    {yearReports.map((r) => (
                      <li key={r.id}>
                        <button
                          type="button"
                          onClick={() => openReport(r.id)}
                          className="w-full flex items-center justify-between gap-3 rounded-xl bg-white border border-gray-100 px-4 py-3 shadow-sm hover:border-gray-200 transition-colors min-h-touch text-left"
                        >
                          <div className="min-w-0 flex-1">
                            <span className="text-sm font-semibold text-gray-900 block truncate">
                              {mbcbReportNamedTitle(r.reportMonth, r.reportYear)}
                            </span>
                            <span className="text-xs text-gray-500">
                              {mbcbMinistryPeriodCaption(r.reportMonth, r.reportYear)}
                            </span>
                          </div>
                          <span
                            className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${statusBadgeClasses(
                              r.status,
                            )}`}
                          >
                            {statusDisplayLabel(r.status)}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No reports yet — start with the card above.</p>
          )}
        </div>

        {!reportsLoading && !reportingDisabled && catchUpPeriods.length > 0 && (
          <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
            <button
              type="button"
              onClick={() => setShowCatchUp((v) => !v)}
              className="flex w-full items-center justify-between text-sm font-semibold text-gray-900"
            >
              Catch up on a past month
              <svg
                className={`w-5 h-5 text-gray-400 transition-transform ${showCatchUp ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showCatchUp && (
              <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
                <p className="text-xs text-gray-600">
                  Start a report for any recent month you haven&apos;t filed yet.
                </p>
                <select
                  value={effectiveSelectedKey}
                  onChange={(e) => setSelectedPeriodKey(e.target.value)}
                  className="block w-full rounded-xl border-gray-200 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500 min-h-touch py-2.5"
                >
                  {catchUpPeriods.map((p) => (
                    <option key={p.key} value={p.key}>
                      {formatMinistryMonthLabel(p.month, p.year)} ministry —{' '}
                      {mbcbReportNamedTitle(p.month, p.year)}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={createSelectedCatchUp}
                  disabled={createReportMutation.isPending || !effectiveSelectedKey}
                  className="w-full py-3 rounded-xl text-sm font-semibold text-primary-700 bg-primary-50 hover:bg-primary-100 disabled:opacity-50 min-h-touch"
                >
                  {createReportMutation.isPending ? 'Creating…' : 'Create report'}
                </button>
                {createError && <p className="text-sm text-red-600">{createError}</p>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MBCBReports;
